import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { database, auth } from '../firebaseConfig';
import { ref as dbRef, get, update, runTransaction } from 'firebase/database';
import VideoPlayer from '../TeamDashboard/Academy/VideoPlayer';
import './AcademyViewer.css';

const COMPLETION_THRESHOLD = 0.7;
const SAVE_INTERVAL_MS = 30_000;
const MAX_VALID_DELTA = 2;
const MILESTONE_COUNT = 10;

const LEVELS = [
    { min: 0,   max: 29,  key: 'starter',     label: 'Starter',      icon: '🌱', color: '#9ca3af' },
    { min: 30,  max: 49,  key: 'learner',      label: 'Learner',      icon: '📖', color: '#60a5fa' },
    { min: 50,  max: 69,  key: 'dedicated',    label: 'Dedicated',    icon: '🔥', color: '#f59e0b' },
    { min: 70,  max: 99,  key: 'expert',       label: 'Expert',       icon: '⚡', color: '#a78bfa' },
    { min: 100, max: 100, key: 'master',       label: 'Master',       icon: '👑', color: '#fbbf24' },
];

function AcademyViewer() {
    const { categoryId } = useParams();
    const navigate = useNavigate();
    const [categories, setCategories] = useState([]);
    const [videos, setVideos] = useState([]);
    const [currentCategory, setCurrentCategory] = useState(null);
    const [currentVideo, setCurrentVideo] = useState(null);
    const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
    const [initialLoading, setInitialLoading] = useState(true);
    const [error, setError] = useState('');
    const [isVideoPlaying, setIsVideoPlaying] = useState(false);
    const [showFullDescription, setShowFullDescription] = useState(false);
    const [viewTransition, setViewTransition] = useState('idle');

    const [isAdmin, setIsAdmin] = useState(false);
    const [hasAccess, setHasAccess] = useState(null);
    const [userTiers, setUserTiers] = useState(null);
    const dataLoadedRef = useRef(false);

    // --- Progress tracking state & refs ---
    const [userProgress, setUserProgress] = useState({});
    const watchedSecondsRef = useRef(0);
    const lastTimeRef = useRef(0);
    const completedRef = useRef(false);
    const saveTimerRef = useRef(null);
    const iframeTimerRef = useRef(null);
    const currentVideoIdRef = useRef(null);

    // Check hasAcademy flag - redirect if not enabled
    useEffect(() => {
        const checkAcademyAccess = async () => {
            if (auth.currentUser) {
                try {
                    const userRef = dbRef(database, `Users/${auth.currentUser.uid}/hasAcademy`);
                    const snapshot = await get(userRef);
                    if (snapshot.exists() && snapshot.val() === true) {
                        setHasAccess(true);
                        const tiersRef = dbRef(database, `Users/${auth.currentUser.uid}/academyTiers`);
                        const tiersSnap = await get(tiersRef);
                        setUserTiers(tiersSnap.exists() ? tiersSnap.val() : null);
                    } else {
                        setHasAccess(false);
                        navigate('/', { replace: true });
                    }
                } catch (err) {
                    setHasAccess(false);
                    navigate('/', { replace: true });
                }
            }
        };
        checkAcademyAccess();
    }, [navigate]);

    // Check if user can access TeamDashboard content (via Custom Claims)
    useEffect(() => {
        const checkAdmin = async () => {
            if (auth.currentUser) {
                try {
                    const tokenResult = await auth.currentUser.getIdTokenResult();
                    setIsAdmin(tokenResult.claims.admin === true);
                } catch (err) {
                    setIsAdmin(false);
                }
            }
        };
        checkAdmin();
    }, []);

    const canAccessTeamDashboard = () => isAdmin;

    // Load academy data only once on mount
    useEffect(() => {
        if (dataLoadedRef.current) return;

        const loadAcademyData = async () => {
            try {
                if (!auth.currentUser) {
                    setError('Please sign in to access the Academy');
                    setInitialLoading(false);
                    return;
                }
                
                let allCategories = [];
                let allVideos = [];
                
                const categoriesRef = dbRef(database, 'Academy/categories');
                const categoriesSnapshot = await get(categoriesRef);
                if (categoriesSnapshot.exists()) {
                    const categoriesData = categoriesSnapshot.val();
                    allCategories = Object.entries(categoriesData).map(([id, category]) => ({
                        ...category,
                        id
                    }));
                }
                
                const videosRef = dbRef(database, 'Academy/videos');
                const videosSnapshot = await get(videosRef);
                if (videosSnapshot.exists()) {
                    const videosData = videosSnapshot.val();
                    allVideos = Object.entries(videosData).map(([id, video]) => ({
                        ...video,
                        id
                    }));
                }
                
                setCategories(allCategories);
                setVideos(allVideos);
                dataLoadedRef.current = true;
                
            } catch (error) {
                console.error('Error loading academy data:', error);
                setError('Failed to load academy data');
            } finally {
                setInitialLoading(false);
            }
        };

        loadAcademyData();
    }, []);

    // Handle categoryId changes from URL without reloading data
    useEffect(() => {
        if (!dataLoadedRef.current) return;

        if (categoryId) {
            const category = categories.find(c => c.id === categoryId);
            setCurrentCategory(category || null);
            
            if (category) {
                const categoryVids = videos
                    .filter(v => v.categoryId === categoryId)
                    .sort((a, b) => (a.order || 0) - (b.order || 0));
                
                if (categoryVids.length > 0) {
                    setCurrentVideo(categoryVids[0]);
                    setCurrentVideoIndex(0);
                    resetTrackingForVideo(categoryVids[0].id);
                }
            }
            setIsVideoPlaying(false);
            setShowFullDescription(false);
            setViewTransition('entering-category');
            requestAnimationFrame(() => setViewTransition('idle'));
        } else {
            setCurrentCategory(null);
            setCurrentVideo(null);
            setCurrentVideoIndex(0);
            setIsVideoPlaying(false);
            setShowFullDescription(false);
            setViewTransition('entering-overview');
            requestAnimationFrame(() => setViewTransition('idle'));
        }
    }, [categoryId, categories, videos]);

    const accessibleCategories = useMemo(() => {
        if (!userTiers) return [];
        return categories.filter(c => {
            const tier = c.tier || 'team';
            return userTiers[tier] === true;
        });
    }, [categories, userTiers]);

    // Redirect if user navigates directly to a category they don't have tier access to
    useEffect(() => {
        if (!initialLoading && currentCategory && hasAccess) {
            const tier = currentCategory.tier || 'team';
            if (!userTiers || userTiers[tier] !== true) {
                navigate('/academy', { replace: true });
            }
        }
    }, [currentCategory, userTiers, hasAccess, initialLoading, navigate]);

    const getCurrentCategoryVideos = () => {
        if (!currentCategory) return [];
        return videos
            .filter(v => v.categoryId === currentCategory.id)
            .sort((a, b) => (a.order || 0) - (b.order || 0));
    };

    // --- Progress tracking ---

    const saveProgressToDb = useCallback((videoId, seconds, isComplete) => {
        const uid = auth.currentUser?.uid;
        if (!uid || !videoId) return;
        const progressRef = dbRef(database, `Academy/progress/${uid}/${videoId}`);
        const data = { watchedSeconds: Math.round(seconds), lastUpdated: Date.now() };
        if (isComplete) {
            data.completed = true;
            data.completedAt = new Date().toISOString();
        }
        update(progressRef, data).catch(() => {});
    }, []);

    const flushProgress = useCallback(() => {
        const vid = currentVideoIdRef.current;
        if (vid && watchedSecondsRef.current > 0) {
            const seconds = Math.round(watchedSecondsRef.current);
            const isComplete = completedRef.current;
            saveProgressToDb(vid, seconds, isComplete);
            setUserProgress(prev => {
                const existing = prev[vid];
                if (existing && existing.watchedSeconds >= seconds && !isComplete) return prev;
                return {
                    ...prev,
                    [vid]: { ...existing, watchedSeconds: seconds, ...(isComplete ? { completed: true } : {}) }
                };
            });
        }
    }, [saveProgressToDb]);

    // Load all user progress once on mount
    useEffect(() => {
        const loadProgress = async () => {
            const uid = auth.currentUser?.uid;
            if (!uid) return;
            try {
                const snap = await get(dbRef(database, `Academy/progress/${uid}`));
                if (snap.exists()) {
                    const data = snap.val();
                    delete data._summary;
                    setUserProgress(data);
                }
            } catch (_) {}
        };
        loadProgress();
    }, []);

    // Sync tracking refs when userProgress loads (fixes race condition on initial render)
    useEffect(() => {
        const vid = currentVideoIdRef.current;
        if (!vid) return;
        const existing = userProgress[vid];
        if (!existing) return;
        if (existing.watchedSeconds > watchedSecondsRef.current) {
            watchedSecondsRef.current = existing.watchedSeconds;
        }
        if (existing.completed) {
            completedRef.current = true;
        }
    }, [userProgress]);

    // Periodic save for long videos + save on tab close
    useEffect(() => {
        saveTimerRef.current = setInterval(flushProgress, SAVE_INTERVAL_MS);
        const handleUnload = () => flushProgress();
        window.addEventListener('beforeunload', handleUnload);
        return () => {
            clearInterval(saveTimerRef.current);
            if (iframeTimerRef.current) clearInterval(iframeTimerRef.current);
            window.removeEventListener('beforeunload', handleUnload);
            flushProgress();
        };
    }, [flushProgress]);

    const resetTrackingForVideo = useCallback((videoId) => {
        // Flush any previous video's progress before resetting
        flushProgress();
        currentVideoIdRef.current = videoId;
        const existing = userProgress[videoId];
        watchedSecondsRef.current = existing?.watchedSeconds || 0;
        completedRef.current = existing?.completed || false;
        lastTimeRef.current = 0;
        // Stop any iframe timer from previous video
        if (iframeTimerRef.current) {
            clearInterval(iframeTimerRef.current);
            iframeTimerRef.current = null;
        }
    }, [userProgress, flushProgress]);

    const [completionToast, setCompletionToast] = useState(null);

    const markComplete = useCallback((videoId) => {
        if (completedRef.current) return;
        completedRef.current = true;
        saveProgressToDb(videoId, watchedSecondsRef.current, true);
        setUserProgress(prev => ({
            ...prev,
            [videoId]: { ...prev[videoId], completed: true, completedAt: new Date().toISOString(), watchedSeconds: Math.round(watchedSecondsRef.current) }
        }));
        setCompletionToast(videoId);
        setTimeout(() => setCompletionToast(null), 3500);
    }, [saveProgressToDb]);

    const handleTimeUpdate = useCallback((e) => {
        const { currentTime, duration } = e.target;
        if (!duration || duration <= 0) return;
        const delta = currentTime - lastTimeRef.current;
        if (delta > 0 && delta < MAX_VALID_DELTA) {
            watchedSecondsRef.current += delta;
        }
        lastTimeRef.current = currentTime;
        if (!completedRef.current && watchedSecondsRef.current >= duration * COMPLETION_THRESHOLD) {
            markComplete(currentVideoIdRef.current);
        }
    }, [markComplete]);

    const startIframeTimer = useCallback((videoId, durationSec) => {
        if (iframeTimerRef.current) clearInterval(iframeTimerRef.current);
        if (!durationSec || durationSec <= 0) return;
        iframeTimerRef.current = setInterval(() => {
            watchedSecondsRef.current += 1;
            if (!completedRef.current && watchedSecondsRef.current >= durationSec * COMPLETION_THRESHOLD) {
                markComplete(videoId);
            }
        }, 1000);
    }, [markComplete]);

    const isVideoCompleted = useCallback((videoId) => {
        return userProgress[videoId]?.completed === true;
    }, [userProgress]);

    const getVideoWatchPercent = useCallback((video) => {
        const prog = userProgress[video.id];
        if (!prog || !prog.watchedSeconds) return 0;
        if (prog.completed) return 100;
        const duration = video.duration || video.videoMetadata?.duration;
        if (!duration || duration <= 0) return 0;
        return Math.min(100, Math.round((prog.watchedSeconds / duration) * 100));
    }, [userProgress]);

    const getCategoryProgress = useCallback((catId) => {
        const catVids = videos.filter(v => v.categoryId === catId);
        if (catVids.length === 0) return { watched: 0, total: 0, percent: 0 };
        const watched = catVids.filter(v => userProgress[v.id]?.completed).length;
        return { watched, total: catVids.length, percent: Math.round((watched / catVids.length) * 100) };
    }, [videos, userProgress]);

    const getOverallProgress = useCallback(() => {
        const accessibleVids = videos.filter(v => accessibleCategories.some(c => c.id === v.categoryId));
        if (accessibleVids.length === 0) return { watched: 0, total: 0, percent: 0 };
        const watched = accessibleVids.filter(v => userProgress[v.id]?.completed).length;
        return { watched, total: accessibleVids.length, percent: Math.round((watched / accessibleVids.length) * 100) };
    }, [videos, accessibleCategories, userProgress]);

    const getNextUnwatched = useCallback((catId) => {
        const catVids = videos.filter(v => v.categoryId === catId).sort((a, b) => (a.order || 0) - (b.order || 0));
        return catVids.find(v => !userProgress[v.id]?.completed);
    }, [videos, userProgress]);

    const getUserLevel = useCallback((percent) => {
        return LEVELS.find(l => percent >= l.min && percent <= l.max) || LEVELS[0];
    }, []);

    const getTotalCompleted = useCallback(() => {
        return Object.values(userProgress).filter(p => p?.completed).length;
    }, [userProgress]);

    const hasMilestoneBadge = getTotalCompleted() >= MILESTONE_COUNT;

    // Persist level + milestone to RTDB so admin can query it
    useEffect(() => {
        const uid = auth.currentUser?.uid;
        if (!uid || videos.length === 0) return;
        const overall = getOverallProgress();
        const level = getUserLevel(overall.percent);
        const totalDone = getTotalCompleted();
        update(dbRef(database, `Academy/progress/${uid}/_summary`), {
            level: level.key,
            percent: overall.percent,
            watched: overall.watched,
            total: overall.total,
            milestone10: totalDone >= MILESTONE_COUNT,
            totalCompleted: totalDone,
            updatedAt: Date.now()
        }).catch(() => {});
    }, [userProgress, videos, getOverallProgress, getUserLevel, getTotalCompleted]);

    const [showLevelInfo, setShowLevelInfo] = useState(false);

    // --- Video navigation ---

    const switchToVideo = useCallback((video, index, autoPlay) => {
        resetTrackingForVideo(video.id);
        setCurrentVideo(video);
        setCurrentVideoIndex(index);
        setIsVideoPlaying(autoPlay);
        setShowFullDescription(false);
        if (autoPlay && video?.id) {
            const viewRef = dbRef(database, `Academy/videos/${video.id}/viewCount`);
            runTransaction(viewRef, (current) => (current || 0) + 1).catch(() => {});
            const isIframe = video.videoSource === 'url' && (
                video.videoUrl?.includes('youtube.com') || video.videoUrl?.includes('youtu.be') || video.videoUrl?.includes('vimeo.com')
            );
            if (isIframe) {
                const dur = video.videoMetadata?.duration || video.duration;
                startIframeTimer(video.id, dur);
            }
        }
    }, [resetTrackingForVideo, startIframeTimer]);

    const handlePlayVideo = () => {
        setIsVideoPlaying(true);
        if (currentVideo?.id) {
            resetTrackingForVideo(currentVideo.id);
            const viewRef = dbRef(database, `Academy/videos/${currentVideo.id}/viewCount`);
            runTransaction(viewRef, (current) => (current || 0) + 1).catch(() => {});
            // Start iframe timer for non-HTML5 videos
            const isIframe = currentVideo.videoSource === 'url' && (
                currentVideo.videoUrl?.includes('youtube.com') || currentVideo.videoUrl?.includes('youtu.be') || currentVideo.videoUrl?.includes('vimeo.com')
            );
            if (isIframe) {
                const dur = currentVideo.videoMetadata?.duration || currentVideo.duration;
                startIframeTimer(currentVideo.id, dur);
            }
        }
    };

    const handleNextVideo = () => {
        const categoryVideos = getCurrentCategoryVideos();
        const nextIndex = currentVideoIndex + 1;
        if (nextIndex < categoryVideos.length) {
            switchToVideo(categoryVideos[nextIndex], nextIndex, true);
        }
    };

    const handlePreviousVideo = () => {
        const categoryVideos = getCurrentCategoryVideos();
        const prevIndex = currentVideoIndex - 1;
        if (prevIndex >= 0) {
            switchToVideo(categoryVideos[prevIndex], prevIndex, true);
        }
    };

    const handleCategorySelect = (category) => {
        setViewTransition('leaving-overview');
        setTimeout(() => {
            navigate(`/academy/${category.id}`);
        }, 200);
    };

    // Helper function to count sentences in description
    const countSentences = (text) => {
        if (!text) return 0;
        // Split by periods, exclamation marks, or question marks followed by space or end of string
        const sentences = text.split(/[.!?](?:\s|$)/).filter(s => s.trim().length > 0);
        return sentences.length;
    };

    // Helper function to get truncated description (first 2 sentences)
    const getTruncatedDescription = (text) => {
        if (!text) return '';
        const sentences = text.split(/([.!?](?:\s|$))/);
        if (sentences.length <= 3) return text; // If 2 or fewer sentences, return full text
        
        // Take first 2 sentences with their punctuation
        return sentences.slice(0, 3).join('').trim();
    };

    const handleVideoSelect = (video, index) => {
        switchToVideo(video, index, false);
    };

    // Helper functions for video URLs
    const getYouTubeId = (url) => {
        const regex = /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/;
        const match = url.match(regex);
        return match ? match[1] : null;
    };

    const getVimeoId = (url) => {
        const regex = /vimeo\.com\/([0-9]+)/;
        const match = url.match(regex);
        return match ? match[1] : null;
    };

    // Helper function to format video duration
    const formatDuration = (seconds) => {
        if (!seconds || isNaN(seconds)) return '0:00';
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    if (hasAccess === null || initialLoading) {
        return (
            <div className="sg-academy-container">
                <div className="sg-academy-loading-placeholder" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="sg-academy-error">
                <h2>Error</h2>
                <p>{error}</p>
            </div>
        );
    }

    if (categories.length === 0 || accessibleCategories.length === 0) {
        return (
            <div className="sg-academy-empty">
                <h2>No Academy Content Available</h2>
                <p>There are no learning categories available for your access level.</p>
            </div>
        );
    }

    const categoryVideos = getCurrentCategoryVideos();

    return (
        <div className={`sg-academy-container ${currentCategory ? 'sg-academy-video-mode' : ''}`}>
            {/* Header */}
            <header className="sg-academy-header">
                <div className="sg-academy-header-content">
                    <button 
                        className="sg-academy-back-btn"
                        onClick={() => navigate('/')}
                        title="Back to Home"
                    >
                        ←
                    </button>
                    <img 
                        src="/img/SalesGod_Academy_Logo_White.png" 
                        alt="SalesGod Academy" 
                        className="sg-academy-logo"
                    />
                </div>
            </header>

            {/* Main Content */}
            <div className="sg-academy-main">
                {!currentCategory ? (
                    /* Categories Overview */
                    <div className={`sg-academy-categories-overview ${viewTransition === 'leaving-overview' ? 'sg-academy-fade-out-left' : ''}`}>
                        {(() => {
                            const overall = getOverallProgress();
                            if (overall.total === 0) return null;
                            const r = 54;
                            const circ = 2 * Math.PI * r;
                            const offset = circ - (overall.percent / 100) * circ;
                            const level = getUserLevel(overall.percent);
                            return (
                                <div className="sg-academy-hero-progress">
                                    <div className="sg-academy-hero-left">
                                        <div className="sg-academy-hero-ring">
                                            <svg viewBox="0 0 120 120" className="sg-academy-ring-svg">
                                                <circle cx="60" cy="60" r={r} className="sg-academy-ring-bg" />
                                                <circle cx="60" cy="60" r={r} className="sg-academy-ring-fill" strokeDasharray={circ} strokeDashoffset={offset} />
                                            </svg>
                                            <div className="sg-academy-ring-label">
                                                <span className="sg-academy-ring-percent">{overall.percent}%</span>
                                            </div>
                                        </div>
                                        <div className="sg-academy-hero-info">
                                            <h2 className="sg-academy-hero-title">
                                                Your Progress
                                                <button className="sg-academy-info-btn" onClick={(e) => { e.stopPropagation(); setShowLevelInfo(true); }} title="Level Info">?</button>
                                            </h2>
                                            <p className="sg-academy-hero-stat">
                                                {overall.watched} of {overall.total} videos completed
                                            </p>
                                            {overall.percent === 100 ? (
                                                <span className="sg-academy-hero-badge sg-academy-hero-badge--done">All Complete ✓</span>
                                            ) : overall.percent > 0 ? (
                                                <span className="sg-academy-hero-badge">Keep going!</span>
                                            ) : (
                                                <span className="sg-academy-hero-badge">Start your first video</span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="sg-academy-hero-right">
                                        <div className="sg-academy-level-badge" style={{ borderColor: level.color, color: level.color, boxShadow: `0 4px 20px ${level.color}15, 0 0 40px ${level.color}08` }}>
                                            <span className="sg-academy-level-icon">{level.icon}</span>
                                            <span className="sg-academy-level-label" style={{ color: level.color }}>{level.label}</span>
                                        </div>
                                        {hasMilestoneBadge && (
                                            <div className="sg-academy-milestone-badge">
                                                <span className="sg-academy-milestone-icon">🏆</span>
                                                <span className="sg-academy-milestone-label">First 10</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })()}
                        <div className="sg-academy-overview-header">
                            <h2>Your Courses</h2>
                        </div>
                        
                        <div className="sg-academy-categories-grid">
                            {accessibleCategories.length === 0 ? (
                                <div className="sg-academy-empty-state">
                                    <h3>No Categories Available</h3>
                                    <p>There are no learning categories available for your access level.</p>
                                </div>
                            ) : (
                                accessibleCategories.map(category => {
                                    const prog = getCategoryProgress(category.id);
                                    const nextVid = getNextUnwatched(category.id);
                                    const isComplete = prog.total > 0 && prog.percent === 100;
                                    return (
                                        <div 
                                            key={category.id} 
                                            className={`sg-academy-category-card ${isComplete ? 'sg-academy-category-card--complete' : ''}`}
                                            onClick={() => handleCategorySelect(category)}
                                        >
                                            <div className="sg-academy-category-image-large">
                                                {category.imageUrl ? (
                                                    <img src={category.imageUrl} alt={category.name} />
                                                ) : (
                                                    <div className="sg-academy-category-placeholder-large">📚</div>
                                                )}
                                                {isComplete && (
                                                    <div className="sg-academy-category-complete-badge">✓ Complete</div>
                                                )}
                                            </div>
                                            
                                            <div className="sg-academy-category-content">
                                                <h3>{category.name}</h3>
                                                <p>{category.description}</p>
                                                
                                                {prog.total > 0 && (
                                                    <div className="sg-academy-category-progress">
                                                        <div className="sg-academy-progress-bar">
                                                            <div className={`sg-academy-progress-fill ${isComplete ? 'sg-academy-progress-fill--done' : ''}`} style={{ width: `${prog.percent}%` }} />
                                                        </div>
                                                        <span className="sg-academy-progress-label">
                                                            {prog.watched}/{prog.total}
                                                        </span>
                                                    </div>
                                                )}
                                                <div className="sg-academy-category-footer">
                                                    <span className="sg-academy-video-count">
                                                        {prog.total} videos
                                                    </span>
                                                    {!isComplete && nextVid && prog.watched > 0 && (
                                                        <span className="sg-academy-continue-label">Continue →</span>
                                                    )}
                                                    {!isComplete && prog.watched === 0 && (
                                                        <span className="sg-academy-start-label">Start →</span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })
                            
                            )}
                        </div>
                    </div>
                ) : (
                    /* Category Video View */
                    <div className={`sg-academy-category-view ${viewTransition === 'leaving-category' ? 'sg-academy-fade-out-right' : ''}`}>
                        {/* Navigation Breadcrumb */}
                        <div className="sg-academy-breadcrumb">
                            <button 
                                onClick={() => {
                                    setViewTransition('leaving-category');
                                    setTimeout(() => navigate('/academy'), 200);
                                }}
                                className="sg-academy-breadcrumb-btn"
                            >
                                ← All Categories
                            </button>
                            <span className="sg-academy-breadcrumb-divider">/</span>
                            <span className="sg-academy-breadcrumb-current">{currentCategory.name}</span>
                            {(() => {
                                const prog = getCategoryProgress(currentCategory.id);
                                if (prog.total === 0) return null;
                                return (
                                    <div className="sg-academy-breadcrumb-progress">
                                        <div className="sg-academy-progress-bar sg-academy-progress-bar--small">
                                            <div className="sg-academy-progress-fill" style={{ width: `${prog.percent}%` }} />
                                        </div>
                                        <span className="sg-academy-progress-label sg-academy-progress-label--small">
                                            {prog.watched}/{prog.total}
                                        </span>
                                    </div>
                                );
                            })()}
                        </div>

                        <div className="sg-academy-video-layout">
                            {/* Video Player Section */}
                            <div className="sg-academy-video-section">
                                {currentVideo ? (
                                    <>
                                        <div className="sg-academy-player-container">
                                            {!isVideoPlaying ? (
                                                <div className="sg-academy-video-thumbnail-container">
                                                    <div className="sg-academy-main-thumbnail">
                                                        {currentVideo.thumbnailUrl ? (
                                                            <img src={currentVideo.thumbnailUrl} alt={currentVideo.title} />
                                                        ) : (
                                                            <div className="sg-academy-main-thumbnail-placeholder">
                                                                📹
                                                            </div>
                                                        )}
                                                        <button 
                                                            className="sg-academy-play-button"
                                                            onClick={handlePlayVideo}
                                                        >
                                                            <span className="sg-academy-play-icon">▶</span>
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="sg-academy-inline-video-player">
                                                    {/* Inline Video Player */}
                                                    {currentVideo.videoSource === 'url' && (currentVideo.videoUrl.includes('youtube.com') || currentVideo.videoUrl.includes('youtu.be')) ? (
                                                        <iframe
                                                            key={currentVideo.id} // Force re-render when video changes
                                                            src={`https://www.youtube.com/embed/${getYouTubeId(currentVideo.videoUrl)}?autoplay=1&controls=1&rel=0`}
                                                            title={currentVideo.title}
                                                            frameBorder="0"
                                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                            allowFullScreen
                                                            className="sg-academy-video-iframe"
                                                        />
                                                    ) : currentVideo.videoSource === 'url' && currentVideo.videoUrl.includes('vimeo.com') ? (
                                                        <iframe
                                                            key={currentVideo.id} // Force re-render when video changes
                                                            src={`https://player.vimeo.com/video/${getVimeoId(currentVideo.videoUrl)}?autoplay=1&controls=1`}
                                                            title={currentVideo.title}
                                                            frameBorder="0"
                                                            allow="autoplay; fullscreen; picture-in-picture"
                                                            allowFullScreen
                                                            className="sg-academy-video-iframe"
                                                        />
                                                    ) : (
                                                        <video
                                                            key={currentVideo.id}
                                                            controls
                                                            autoPlay
                                                            controlsList="nodownload"
                                                            onContextMenu={(e) => e.preventDefault()}
                                                            onTimeUpdate={handleTimeUpdate}
                                                            className="sg-academy-video-element"
                                                            poster={currentVideo.thumbnailUrl}
                                                        >
                                                            <source src={currentVideo.videoUrl} type="video/mp4" />
                                                            Your browser does not support the video tag.
                                                        </video>
                                                    )}
                                                    

                                                </div>
                                            )}
                                        </div>
                                        
                                        <div className="sg-academy-video-info">
                                            <div className="sg-academy-video-meta">
                                                <span className="sg-academy-video-progress">
                                                    Video {currentVideoIndex + 1} of {categoryVideos.length}
                                                </span>
                                                <span className="sg-academy-category-name">{currentCategory.name}</span>
                                            </div>
                                            
                                            <h2 className="sg-academy-video-title">{currentVideo.title}</h2>
                                            
                                            {currentVideo.description && (
                                                <div className="sg-academy-video-description-container">
                                                    <p className="sg-academy-video-description">
                                                        {showFullDescription 
                                                            ? currentVideo.description 
                                                            : getTruncatedDescription(currentVideo.description)}
                                                    </p>
                                                    {countSentences(currentVideo.description) > 2 && (
                                                        <button 
                                                            className="sg-academy-show-more-btn"
                                                            onClick={() => setShowFullDescription(!showFullDescription)}
                                                        >
                                                            {showFullDescription ? 'Show Less' : 'Show More'}
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                            

                                            
                                            <div className="sg-academy-video-controls">
                                                <button
                                                    onClick={handlePreviousVideo}
                                                    disabled={currentVideoIndex === 0}
                                                    className="sg-academy-control-btn sg-academy-prev-btn"
                                                >
                                                    ← Previous
                                                </button>
                                                
                                                <button
                                                    onClick={handleNextVideo}
                                                    disabled={currentVideoIndex === categoryVideos.length - 1}
                                                    className="sg-academy-control-btn sg-academy-next-btn"
                                                >
                                                    Next →
                                                </button>
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <div className="sg-academy-no-video">
                                        <h3>No videos available in this category</h3>
                                        <p>This category doesn't have any videos yet.</p>
                                    </div>
                                )}
                            </div>

                            {/* Video Playlist Sidebar */}
                            <div className="sg-academy-playlist-sidebar">
                                <h3 className="sg-academy-playlist-title">Course Videos</h3>
                                <div className="sg-academy-playlist-content">
                                    {categoryVideos.length > 0 ? (
                                        categoryVideos.map((video, index) => (
                                            <button
                                                key={video.id}
                                                onClick={() => handleVideoSelect(video, index)}
                                                className={`sg-academy-playlist-item ${currentVideo?.id === video.id ? 'sg-academy-playlist-active' : ''} ${isVideoCompleted(video.id) ? 'sg-academy-playlist-completed' : ''}`}
                                            >
                                                <div className="sg-academy-playlist-thumbnail">
                                                    {video.thumbnailUrl ? (
                                                        <img src={video.thumbnailUrl} alt={video.title} />
                                                    ) : (
                                                        <div className="sg-academy-thumbnail-placeholder">📹</div>
                                                    )}
                                                    {isVideoCompleted(video.id) ? (
                                                        <div className="sg-academy-playlist-check">✓</div>
                                                    ) : (
                                                        <div className="sg-academy-playlist-number">{index + 1}</div>
                                                    )}
                                                    {(video.duration || video.videoMetadata?.duration) && (
                                                        <div className="sg-academy-video-duration">
                                                            {formatDuration(video.duration || video.videoMetadata?.duration)}
                                                        </div>
                                                    )}
                                                    <div className="sg-academy-playlist-video-progress-bar">
                                                        <div
                                                            className={`sg-academy-playlist-video-progress-fill ${isVideoCompleted(video.id) ? 'sg-academy-playlist-video-progress-fill--done' : ''}`}
                                                            style={{ width: `${getVideoWatchPercent(video)}%` }}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="sg-academy-playlist-info">
                                                    <div className="sg-academy-playlist-video-title">{video.title}</div>
                                                </div>
                                            </button>
                                        ))
                                    ) : (
                                        <p className="sg-academy-empty-playlist">No videos in this category</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {showLevelInfo && (
                <div className="sg-academy-modal-overlay" onClick={() => setShowLevelInfo(false)}>
                    <div className="sg-academy-level-modal" onClick={e => e.stopPropagation()}>
                        <div className="sg-academy-level-modal-header">
                            <h3>Academy Levels</h3>
                            <button className="sg-academy-modal-close" onClick={() => setShowLevelInfo(false)}>✕</button>
                        </div>
                        <p className="sg-academy-level-modal-desc">Your level is based on the percentage of all available videos you've completed. New videos can change your level — keep watching to stay on top!</p>
                        <div className="sg-academy-level-list">
                            {LEVELS.map(l => {
                                const current = getUserLevel(getOverallProgress().percent);
                                const isActive = l.key === current.key;
                                return (
                                    <div key={l.key} className={`sg-academy-level-row ${isActive ? 'sg-academy-level-row--active' : ''}`}>
                                        <span className="sg-academy-level-row-icon">{l.icon}</span>
                                        <div className="sg-academy-level-row-info">
                                            <span className="sg-academy-level-row-name" style={{ color: isActive ? l.color : undefined }}>{l.label}</span>
                                            <span className="sg-academy-level-row-range">{l.min}% – {l.max}%</span>
                                        </div>
                                        {isActive && <span className="sg-academy-level-row-current">You</span>}
                                    </div>
                                );
                            })}
                        </div>
                        <div className="sg-academy-milestone-section">
                            <div className="sg-academy-milestone-section-title">Fixed Badge</div>
                            <div className={`sg-academy-milestone-row ${hasMilestoneBadge ? 'sg-academy-milestone-row--earned' : ''}`}>
                                <span className="sg-academy-milestone-row-icon">🏆</span>
                                <div className="sg-academy-milestone-row-info">
                                    <span className="sg-academy-milestone-row-name">First 10 Badge</span>
                                    <span className="sg-academy-milestone-row-desc">Complete your first 10 videos (never lost)</span>
                                </div>
                                {hasMilestoneBadge ? (
                                    <span className="sg-academy-milestone-row-earned">Earned ✓</span>
                                ) : (
                                    <span className="sg-academy-milestone-row-progress">{getTotalCompleted()}/10</span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {completionToast && (
                <div className="sg-academy-completion-toast" key={completionToast}>
                    <div className="sg-academy-toast-icon">✓</div>
                    <span>Video completed!</span>
                </div>
            )}
        </div>
    );
}

export default AcademyViewer;
